import { http } from '../lib/api/http';

const BASE_PATH = '/service-reviews';

const parseRatingValue = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const mapUserSummary = record => {
  if (!record) return null;
  return {
    id: record.id || '',
    name:
      record.display_name ||
      record.displayName ||
      record.name ||
      record.username ||
      record.email ||
      'Cliente Flotte',
    avatarUrl: record.avatar_url || record.avatarUrl || '',
    companyName: record.company_name || record.companyName || '',
  };
};

export const mapReviewRecord = record => {
  if (!record) return null;
  const author = mapUserSummary(record.author);
  return {
    id: record.id,
    reservationId: record.reservation_id || record.reservation || '',
    direction: record.direction || 'client_to_provider',
    targetCompanyId: record.target_company_id || record.targetCompany || '',
    targetUserId: record.target_user_id || record.targetUser || '',
    authorId: record.author_id || record.author || '',
    author,
    authorName: author?.name || '',
    overall: parseRatingValue(record.overall),
    quality: parseRatingValue(record.quality),
    price: parseRatingValue(record.price),
    punctuality: parseRatingValue(record.punctuality),
    comment: typeof record.comment === 'string' ? record.comment : '',
    status: record.status || 'completed',
    created: record.created_at || record.created,
    updated: record.updated_at || record.updated,
  };
};

const sanitizeComment = value => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const RATING_KEYS = ['overall', 'quality', 'price', 'punctuality'];

export async function createServiceReview(payload = {}) {
  const reservationId = payload.reservationId || payload.reservation;
  if (!reservationId) {
    throw new Error('La reseña requiere una reserva asociada.');
  }

  const direction = payload.direction || 'client_to_provider';
  const targetCompanyId = payload.targetCompanyId || payload.companyId || '';
  const targetUserId = payload.targetUserId || payload.userId || '';

  if (direction === 'client_to_provider' && !targetCompanyId) {
    throw new Error('Falta la empresa a calificar.');
  }
  if (direction === 'provider_to_client' && !targetUserId) {
    throw new Error('Falta el cliente a calificar.');
  }

  const body = {
    reservation_id: reservationId,
    direction: direction,
    comment: sanitizeComment(payload.comment),
    status: payload.status || 'completed',
  };

  if (targetCompanyId) {
    body.target_company_id = targetCompanyId;
  }
  if (targetUserId) {
    body.target_user_id = targetUserId;
  }

  RATING_KEYS.forEach(key => {
    const numeric = Number(payload[key]);
    if (Number.isFinite(numeric) && numeric > 0) {
      body[key] = Math.max(1, Math.min(5, numeric));
    }
  });

  const response = await http.post(BASE_PATH, body);
  return mapReviewRecord(response.data);
}

export async function listServiceReviews({
  targetCompanyId,
  targetUserId,
  direction,
  limit = 10,
  page = 1,
} = {}) {
  const params = {
    limit,
    page,
    with_meta: true,
  };
  if (targetCompanyId) {
    params.target_company_id = targetCompanyId;
  }
  if (targetUserId) {
    params.target_user_id = targetUserId;
  }
  if (direction) {
    params.direction = direction;
  }

  const response = await http.get(BASE_PATH, { params });
  const payload = response.data;
  if (Array.isArray(payload)) {
    return {
      items: payload.map(mapReviewRecord),
      totalItems: payload.length,
      page,
      perPage: limit,
    };
  }

  const items = Array.isArray(payload.items) ? payload.items.map(mapReviewRecord) : [];
  return {
    items,
    totalItems:
      typeof payload.total === 'number'
        ? payload.total
        : typeof payload.totalItems === 'number'
        ? payload.totalItems
        : items.length,
    page: payload.page ?? page,
    perPage: payload.per_page ?? payload.perPage ?? limit,
  };
}
